import type { UseDataReturn } from "~/hooks/useData";
import useData from "~/hooks/useData";
import { clearAllCollectionCache } from "~/lib/cache/clearCache";
import type { Account } from "~/types";

interface UseSingleAccountParams {
  id?: string | undefined | null;
  depth?: number;
}

interface UseSingleAccountReturn extends UseDataReturn {
  data: Account | undefined;
}

/**
 * Custom hook for fetching a single contact by ID.
 * Utilizes the `useData` hook to retrieve data from the "accounts" collection.
 *
 * @param {UseSingleAccountParams} params - Parameters for fetching the account.
 * @param {string} params.id - The unique identifier of the account.
 * @param {number} [params.depth] - Optional depth for nested data fetching.
 * @returns {Account | undefined} The fetched contact or undefined if not found.
 */
const useSingleAccount = ({
  id,
  depth,
}: UseSingleAccountParams = {}): UseSingleAccountReturn => {
  const { createItem, updateItem, data, ...rest } = useData({
    collection: "accounts",
    id,
    depth,
  });

  const createAccountItem = async (data: any) => {
    const response = await createItem(data);

    // Organizations cache needs to be cleared after creating an account.
    // This is because accounts are linked to organizations, and we need to ensure
    // that the organization data reflects the newly created account.
    clearAllCollectionCache("organizations");

    return response;
  };

  const updateAccountItem = async (data: any) => {
    const response = await updateItem(data);

    // Organizations cache needs to be cleared after updating an account.
    // This is to ensure that any changes to the account are reflected in the organization data.
    clearAllCollectionCache("organizations");

    return response;
  };

  return {
    data: data as Account | undefined,
    createItem: createAccountItem,
    updateItem: updateAccountItem,
    ...rest,
  };
};

export default useSingleAccount;
